import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import useGetFetchConfig from '../useGetFetchConfig';

type DownloadType = 'original' | 'highlighted' | 'asPDF';

interface UseDownloadDocumentProps {
  caseId: string;
  docId: string;
  fileName?: string;
  downloadType: DownloadType;
}

export const useDownloadDocument = ({ caseId, docId, fileName, downloadType }: UseDownloadDocumentProps) => {
  const { fetchConfigGET } = useGetFetchConfig();

  const downloadTypeLabel = downloadType === 'highlighted' ? 'highlighted ' : downloadType === 'asPDF' ? 'PDF ' : '';

  const downloadDocument = async () => {
    try {
      // Fetch the presigned URL with download type parameter
      const urlResponse = await fetch(
        `${APIBaseChronos}/api/case/${caseId}/docs/${docId}/download?type=${downloadType}`,
        fetchConfigGET,
      );

      if (!urlResponse.ok) {
        const errorText = await urlResponse.text();
        throw new Error(errorText || `Failed to get ${downloadTypeLabel}document URL`);
      }

      const presignedUrl = await urlResponse.text();

      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.target = '_blank';

      // Set the filename for the download
      if (fileName) {
        link.download = fileName;
      } else {
        link.download = `wexler-document-${docId}`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

  return useMutation(downloadDocument, {
    onError: (error: Error) => {
      // Show more specific error message if available
      if (error.message.includes('not available')) {
        toast.error(error.message);
      } else {
        toast.error(`Failed to download ${downloadTypeLabel}document`);
      }
    },
  });
};
